'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { ShoppingCartIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NumberInput } from '@/components/ui/number-input';
import { useMeStore, useCartStore, useLocalCartStore } from '@/store';
import { getErrorMessage, getLocalizedValue } from '@/lib/utils';
import { useAppLocale, useTranslatedRoute } from '@/hooks';
import { ProductType } from '@/types/product';
type PropsType = {
  product: ProductType;
  size?: 'default' | 'large' | 'icon';
  className?: string;
};
const AddToCartAction = ({
  product,
  size = 'default',
  className = ''
}: PropsType) => {
  const t = useTranslations();
  const router = useRouter();
  const {
    locale,
    backendLocale
  } = useAppLocale();
  const {
    buildRoute
  } = useTranslatedRoute();
  const {
    meState
  } = useMeStore();
  const {
    addItem
  } = useCartStore();
  const {
    addItem: addLocalItem
  } = useLocalCartStore();
  const [quantity, setQuantity] = useState<number>(1);
  const [adding, setAdding] = useState<boolean>(false);
  const isDisabled = product.stock < 1;
  const isPending = adding;
  const productTitle = getLocalizedValue(product, 'title', backendLocale) || product.code;
  const cartRoute = `/${locale}${buildRoute('cart')}`;
  const productSlug = getLocalizedValue(product, 'slug', backendLocale);
  const categorySlugs: string[] = [];
  if (product.category?.parents?.length) {
    product.category.parents.forEach(parent => {
      const slug = getLocalizedValue(parent, 'slug', backendLocale);
      if (slug) {
        categorySlugs.push(slug);
      }
    });
  }
  const categorySlug = product.category ? getLocalizedValue(product.category, 'slug', backendLocale) : null;
  if (categorySlug) {
    categorySlugs.push(categorySlug);
  }
  const showCartToast = (name: string) => {
    toast.success(t('item_added_to_cart', {
      code: product.code,
      name,
      quantity
    }), {
      action: {
        label: t('go_to_cart'),
        onClick: () => router.push(cartRoute)
      }
    });
  };
  const handleAddToCart = async () => {
    if (meState.response === null) {
      addLocalItem({
        productId: product.id,
        quantity,
        productCode: product.code,
        productTitle,
        mainPicturePath: product.mainPicture?.path ?? null,
        stock: product.stock,
        productSlug: productSlug || undefined,
        categorySlugs
      });
      showCartToast(productTitle);
      setQuantity(1);
      return;
    }
    try {
      setAdding(true);
      await addItem({
        productId: product.id,
        quantity
      });
      showCartToast(productTitle);
      setQuantity(1);
    } catch (error: any) {
      toast.error(getErrorMessage(error, t('something_went_wrong')));
    } finally {
      setAdding(false);
    }
  };
  return <div className={`flex shrink-0 gap-2 ${className}`}>
      <NumberInput value={quantity} onValueChange={value => setQuantity(value ?? 1)} min={1} max={product.stock} stepper={1} disabled={isDisabled || isPending} className={`w-32! flex-none! ${size === 'icon' ? 'sm:w-28!' : ''}`} inputClassName={`bg-white text-right ${size === 'large' ? 'p-6.5 text-lg! h-14' : ''}`} buttonsClassName={`${size === 'large' ? 'h-7! w-6.5!' : ''}`} />
      <Button size={size === 'icon' ? 'icon' : 'default'} variant="brand" disabled={isDisabled || isPending} onClick={handleAddToCart} className={size === 'large' ? 'h-14 px-10! py-6.5 text-lg' : ''}>
        <ShoppingCartIcon className={size === 'large' ? 'mr-1 h-6 w-6' : 'h-4 w-4'} />
        {size !== 'icon' && <span className="uppercase">{t('add_to_cart')}</span>}
      </Button>
    </div>;
};
export default AddToCartAction;